import { useState, useEffect, useMemo } from 'react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../components/ui/accordion';
import {
    CaretDown,
    FloppyDisk,
    CheckCircle,
    WarningCircle
} from '@phosphor-icons/react';
import { toast } from 'sonner';

// Field type icons and labels
const FIELD_TYPES = {
    text: { label: 'Testo' },
    textarea: { label: 'Area di Testo' },
    number: { label: 'Numero' },
    date: { label: 'Data' },
    select: { label: 'Selezione' },
    radio: { label: 'Radio' },
    checkbox: { label: 'Checkbox' },
    email: { label: 'Email' },
    phone: { label: 'Telefono' },
    hidden: { label: 'Nascosto' },
    readonly: { label: 'Solo Lettura' },
};

// Get width class based on field width setting
const getWidthClass = (width) => {
    switch (width) {
        case 'half': return 'col-span-6';
        case 'third': return 'col-span-4';
        case 'full':
        default: return 'col-span-12';
    }
};

// Single Field Renderer
function FormField({ field, value, onChange, readOnly = false, errors = {}, formData = {} }) {
    const hasError = errors[field.name];
    const isRequired = field.required;
    
    const baseInputClass = `${hasError ? 'border-destructive focus-visible:ring-destructive' : ''}`;
    
    const handleChange = (newValue) => {
        onChange(field.name, newValue);
    };

    // Hidden field
    if (field.type === 'hidden') {
        return <input type="hidden" name={field.name} value={value || field.default_value || ''} />;
    }

    // Readonly field
    if (field.type === 'readonly' || readOnly) {
        return (
            <div className={getWidthClass(field.width)}>
                <Label className="text-sm text-muted-foreground">{field.label}</Label>
                <p className="mt-1 p-2 bg-muted/50 rounded-md text-sm">
                    {value || field.default_value || '-'}
                </p>
            </div>
        );
    }

    // Text field
    if (field.type === 'text' || field.type === 'email' || field.type === 'phone') {
        return (
            <div className={getWidthClass(field.width)}>
                <Label htmlFor={field.name} className="text-sm flex items-center gap-1">
                    {field.label}
                    {isRequired && <span className="text-destructive">*</span>}
                </Label>
                <Input
                    id={field.name}
                    type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
                    value={value || ''}
                    onChange={(e) => handleChange(e.target.value)}
                    placeholder={field.placeholder || ''}
                    className={`mt-1 ${baseInputClass}`}
                    disabled={readOnly}
                />
                {field.help_text && (
                    <p className="text-xs text-muted-foreground mt-1">{field.help_text}</p>
                )}
                {hasError && (
                    <p className="text-xs text-destructive mt-1">{hasError}</p>
                )}
            </div>
        );
    }

    // Textarea field
    if (field.type === 'textarea') {
        return (
            <div className={getWidthClass(field.width)}>
                <Label htmlFor={field.name} className="text-sm flex items-center gap-1">
                    {field.label}
                    {isRequired && <span className="text-destructive">*</span>}
                </Label>
                <Textarea
                    id={field.name}
                    value={value || ''}
                    onChange={(e) => handleChange(e.target.value)}
                    placeholder={field.placeholder || ''}
                    className={`mt-1 ${baseInputClass}`}
                    rows={3}
                    disabled={readOnly}
                />
                {field.help_text && (
                    <p className="text-xs text-muted-foreground mt-1">{field.help_text}</p>
                )}
                {hasError && (
                    <p className="text-xs text-destructive mt-1">{hasError}</p>
                )}
            </div>
        );
    }

    // Number field
    if (field.type === 'number') {
        return (
            <div className={getWidthClass(field.width)}>
                <Label htmlFor={field.name} className="text-sm flex items-center gap-1">
                    {field.label}
                    {isRequired && <span className="text-destructive">*</span>}
                </Label>
                <Input
                    id={field.name}
                    type="number"
                    value={value || ''}
                    onChange={(e) => handleChange(e.target.value)}
                    placeholder={field.placeholder || ''}
                    min={field.validations?.min}
                    max={field.validations?.max}
                    step="any"
                    className={`mt-1 ${baseInputClass}`}
                    disabled={readOnly}
                />
                {field.help_text && (
                    <p className="text-xs text-muted-foreground mt-1">{field.help_text}</p>
                )}
                {hasError && (
                    <p className="text-xs text-destructive mt-1">{hasError}</p>
                )}
            </div>
        );
    }

    // Date field
    if (field.type === 'date') {
        return (
            <div className={getWidthClass(field.width)}>
                <Label htmlFor={field.name} className="text-sm flex items-center gap-1">
                    {field.label}
                    {isRequired && <span className="text-destructive">*</span>}
                </Label>
                <Input
                    id={field.name}
                    type="date"
                    value={value || ''}
                    onChange={(e) => handleChange(e.target.value)}
                    className={`mt-1 ${baseInputClass}`}
                    disabled={readOnly}
                />
                {field.help_text && (
                    <p className="text-xs text-muted-foreground mt-1">{field.help_text}</p>
                )}
                {hasError && (
                    <p className="text-xs text-destructive mt-1">{hasError}</p>
                )}
            </div>
        );
    }

    // Select field
    if (field.type === 'select') {
        return (
            <div className={getWidthClass(field.width)}>
                <Label htmlFor={field.name} className="text-sm flex items-center gap-1">
                    {field.label}
                    {isRequired && <span className="text-destructive">*</span>}
                </Label>
                <Select
                    value={value || '_empty'}
                    onValueChange={(v) => handleChange(v === '_empty' ? '' : v)}
                    disabled={readOnly}
                >
                    <SelectTrigger className={`mt-1 ${baseInputClass}`}>
                        <SelectValue placeholder={field.placeholder || 'Seleziona...'} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_empty">Seleziona...</SelectItem>
                        {(field.options || []).map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {field.help_text && (
                    <p className="text-xs text-muted-foreground mt-1">{field.help_text}</p>
                )}
                {hasError && (
                    <p className="text-xs text-destructive mt-1">{hasError}</p>
                )}
            </div>
        );
    }

    // Radio field
    if (field.type === 'radio') {
        return (
            <div className={getWidthClass(field.width)}>
                <Label className="text-sm flex items-center gap-1 mb-2">
                    {field.label}
                    {isRequired && <span className="text-destructive">*</span>}
                </Label>
                <div className="flex flex-wrap gap-4">
                    {(field.options || []).map((opt) => (
                        <label 
                            key={opt.value}
                            className="flex items-center gap-2 cursor-pointer"
                        >
                            <input
                                type="radio"
                                name={field.name}
                                value={opt.value}
                                checked={value === opt.value}
                                onChange={(e) => handleChange(e.target.value)}
                                disabled={readOnly}
                                className="w-4 h-4 text-primary"
                            />
                            <span className="text-sm">{opt.label}</span>
                        </label>
                    ))}
                </div>
                {field.help_text && (
                    <p className="text-xs text-muted-foreground mt-1">{field.help_text}</p>
                )}
                {hasError && (
                    <p className="text-xs text-destructive mt-1">{hasError}</p>
                )}
            </div>
        );
    }

    // Checkbox field
    if (field.type === 'checkbox') {
        return (
            <div className={getWidthClass(field.width)}>
                <div className="flex items-center gap-2">
                    <Checkbox
                        id={field.name}
                        checked={value === true || value === 'true'}
                        onCheckedChange={(checked) => handleChange(checked)}
                        disabled={readOnly}
                    />
                    <Label htmlFor={field.name} className="text-sm cursor-pointer">
                        {field.label}
                        {isRequired && <span className="text-destructive ml-1">*</span>}
                    </Label>
                </div>
                {field.help_text && (
                    <p className="text-xs text-muted-foreground mt-1 ml-6">{field.help_text}</p>
                )}
                {hasError && (
                    <p className="text-xs text-destructive mt-1 ml-6">{hasError}</p>
                )}
            </div>
        );
    }

    // Checkbox Group field (multiple selections)
    if (field.type === 'checkbox_group') {
        const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
        
        const handleCheckboxGroupChange = (optValue, checked) => {
            let newValues;
            if (checked) {
                newValues = [...selectedValues, optValue];
            } else {
                newValues = selectedValues.filter(v => v !== optValue);
            }
            handleChange(newValues);
        };
        
        return (
            <div className={getWidthClass(field.width)}>
                <Label className="text-sm flex items-center gap-1 mb-2">
                    {field.label}
                    {isRequired && <span className="text-destructive">*</span>}
                </Label>
                <div className="space-y-2">
                    {(field.options || []).map((opt) => (
                        <div key={opt.value} className="flex items-center gap-2">
                            <Checkbox
                                id={`${field.name}_${opt.value}`}
                                checked={selectedValues.includes(opt.value)}
                                onCheckedChange={(checked) => handleCheckboxGroupChange(opt.value, checked)}
                                disabled={readOnly}
                            />
                            <Label htmlFor={`${field.name}_${opt.value}`} className="text-sm cursor-pointer font-normal">
                                {opt.label}
                            </Label>
                        </div>
                    ))}
                </div>
                {field.help_text && (
                    <p className="text-xs text-muted-foreground mt-1">{field.help_text}</p>
                )}
                {hasError && (
                    <p className="text-xs text-destructive mt-1">{hasError}</p>
                )}
            </div>
        );
    }

    // Map Link field (computed link to Google Maps)
    if (field.type === 'map_link') {
        // Get lat/lng from form data
        const lat = formData?.latitudine || formData?.lat;
        const lng = formData?.longitudine || formData?.lng || formData?.lon;
        
        if (lat && lng) {
            const mapUrl = `https://www.google.com/maps?q=${lat},${lng}`;
            return (
                <div className={getWidthClass(field.width)}>
                    <Label className="text-sm flex items-center gap-1 mb-1">
                        {field.label}
                    </Label>
                    <a 
                        href={mapUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                            <circle cx="12" cy="10" r="3"></circle>
                        </svg>
                        Apri in Google Maps ({lat.toFixed ? lat.toFixed(4) : lat}, {lng.toFixed ? lng.toFixed(4) : lng})
                    </a>
                </div>
            );
        }
        
        return (
            <div className={getWidthClass(field.width)}>
                <Label className="text-sm flex items-center gap-1 mb-1 text-muted-foreground">
                    {field.label}
                </Label>
                <p className="text-xs text-muted-foreground">Inserisci latitudine e longitudine per visualizzare la mappa</p>
            </div>
        );
    }

    // Fallback for unknown types
    return (
        <div className={getWidthClass(field.width)}>
            <Label className="text-sm text-muted-foreground">{field.label}</Label>
            <p className="text-xs text-muted-foreground">Tipo campo non supportato: {field.type}</p>
        </div>
    );
}

// Main Form Renderer Component
export default function StepFormRenderer({ 
    sections = [], 
    formData = {}, 
    onChange, 
    onSave,
    readOnly = false,
    saving = false,
    nodeStatus = 'non_iniziato'
}) {
    const [localData, setLocalData] = useState(formData);
    const [errors, setErrors] = useState({});
    const [hasChanges, setHasChanges] = useState(false);
    const sortedSections = useMemo(() => [...sections].sort((a, b) => a.order - b.order), [sections]);

    useEffect(() => {
        setLocalData(formData);
    }, [formData]);

    const handleFieldChange = (fieldName, value) => {
        const newData = { ...localData, [fieldName]: value };
        setLocalData(newData);
        setHasChanges(true);
        
        // Clear error for this field
        if (errors[fieldName]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
            });
        }
        
        if (onChange) {
            onChange(newData);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        sections.forEach(section => {
            (section.fields || []).forEach(field => {
                if (field.required) {
                    const value = localData[field.name];
                    if (value === undefined || value === null || value === '') {
                        newErrors[field.name] = 'Campo obbligatorio';
                    }
                }
                
                // Email validation
                if (field.type === 'email' && localData[field.name]) {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(localData[field.name])) {
                        newErrors[field.name] = 'Email non valida';
                    }
                }
                
                // Number validation
                if (field.type === 'number' && localData[field.name]) {
                    const num = parseFloat(localData[field.name]);
                    if (field.validations?.min !== undefined && num < field.validations.min) {
                        newErrors[field.name] = `Valore minimo: ${field.validations.min}`;
                    }
                    if (field.validations?.max !== undefined && num > field.validations.max) {
                        newErrors[field.name] = `Valore massimo: ${field.validations.max}`;
                    }
                }
            });
        });
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (readOnly) return;
        
        const isValid = validateForm();
        if (!isValid) {
            toast.error('Correggi gli errori nel form prima di salvare');
            return;
        }
        
        if (onSave) {
            await onSave(localData);
            setHasChanges(false);
        }
    };

    // Calculate completion stats
    const totalRequiredFields = sections.reduce((acc, s) => 
        acc + (s.fields || []).filter(f => f.required).length, 0
    );
    const completedRequiredFields = sections.reduce((acc, s) => 
        acc + (s.fields || []).filter(f => f.required && localData[f.name]).length, 0
    );
    const completionPercentage = totalRequiredFields > 0 
        ? Math.round((completedRequiredFields / totalRequiredFields) * 100) 
        : 100;

    if (!sections || sections.length === 0) {
        return (
            <div className="text-center py-8 text-muted-foreground">
                <p>Nessun form configurato per questo step.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Form Header with Save Button */}
            <div className="flex items-center justify-between sticky top-0 bg-background z-10 py-2 border-b">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        {completionPercentage === 100 ? (
                            <CheckCircle weight="fill" className="w-5 h-5 text-green-500" />
                        ) : (
                            <WarningCircle weight="fill" className="w-5 h-5 text-amber-500" />
                        )}
                        <span className="text-sm font-medium">
                            {completedRequiredFields}/{totalRequiredFields} campi obbligatori
                        </span>
                    </div>
                    <Badge variant={completionPercentage === 100 ? 'default' : 'secondary'}>
                        {completionPercentage}% completato
                    </Badge>
                    {hasChanges && (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">
                            Modifiche non salvate
                        </Badge>
                    )}
                </div>
                {!readOnly && nodeStatus === 'in_corso' && (
                    <Button onClick={handleSave} disabled={saving || !hasChanges}>
                        <FloppyDisk className="w-4 h-4 mr-2" />
                        {saving ? 'Salvataggio...' : 'Salva Dati'}
                    </Button>
                )}
            </div>

            {/* Form Sections */}
            <Accordion 
                type="multiple" 
                defaultValue={sections.map(s => s.id)}
                className="space-y-3"
            >
                {sortedSections.map((section) => {
                    const sectionRequiredFields = (section.fields || []).filter(f => f.required).length;
                    const sectionCompletedFields = (section.fields || []).filter(f => f.required && localData[f.name]).length;
                    const sectionComplete = sectionRequiredFields === sectionCompletedFields;
                    
                    return (
                        <AccordionItem 
                            key={section.id} 
                            value={section.id}
                            className="border rounded-lg overflow-hidden bg-white"
                        >
                            <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30">
                                <div className="flex items-center gap-3 flex-1">
                                    {sectionComplete ? (
                                        <CheckCircle weight="fill" className="w-5 h-5 text-green-500" />
                                    ) : (
                                        <WarningCircle weight="fill" className="w-5 h-5 text-amber-500" />
                                    )}
                                    <div className="flex-1 text-left">
                                        <span className="font-medium">{section.title}</span>
                                        {section.description && (
                                            <p className="text-xs text-muted-foreground mt-0.5">
                                                {section.description}
                                            </p>
                                        )}
                                    </div>
                                    <span className="text-xs text-muted-foreground mr-2">
                                        {sectionCompletedFields}/{sectionRequiredFields} obbligatori
                                    </span>
                                </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4">
                                <div className="grid grid-cols-12 gap-4">
                                    {[...(section.fields || [])]
                                        .sort((a, b) => a.order - b.order)
                                        .filter((field) => {
                                            // Check conditional visibility
                                            if (field.conditional) {
                                                const condField = field.conditional.field;
                                                const condValue = field.conditional.value;
                                                const currentValue = localData[condField];
                                                
                                                // Support multiple values (array)
                                                if (Array.isArray(condValue)) {
                                                    return condValue.includes(currentValue);
                                                }
                                                return currentValue === condValue;
                                            }
                                            return true;
                                        })
                                        .map((field) => (
                                            <FormField
                                                key={field.id || field.name}
                                                field={field}
                                                value={localData[field.name]}
                                                onChange={handleFieldChange}
                                                readOnly={readOnly || nodeStatus !== 'in_corso'}
                                                errors={errors}
                                                formData={localData}
                                            />
                                        ))
                                    }
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    );
                })}
            </Accordion>

            {/* Error Summary */}
            {Object.keys(errors).length > 0 && (
                <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
                    <div className="flex items-center gap-2 text-destructive font-medium mb-2">
                        <WarningCircle weight="fill" className="w-4 h-4" />
                        <span>Errori nel form</span>
                    </div>
                    <ul className="text-sm text-destructive/80 space-y-1 ml-6">
                        {Object.entries(errors).map(([field, error]) => (
                            <li key={field}>• {field}: {error}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
